import React, { useEffect, useState } from "react";
import { Col, Dropdown, Form, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import StbButton from "../../../atoms/button/StbButton";
import { IModalEditContactProps } from "./ModalEditContact.types";
import { useQueryClient } from "@tanstack/react-query";
import { getGetApiContactsCustomerCustomerNumberQueryKey, useDeleteApiContactsContactId, usePutApiContacts } from "../../../../api/generated/endpoints/contact/contact";
import type { ContactSummaryDto, ContactUpdateDto } from "../../../../api/generated/model";
import { DeleteOutlined, ImportOutlined } from "@ant-design/icons";
import Tooltip from "antd/lib/tooltip";
import Alert from "react-bootstrap/Alert";

interface FormErrors {
  name?: string;
  phoneNumbers: string[];
  emailAddresses: string[];
}

const ModalEditContact: React.FC<IModalEditContactProps> = (props) => {
  const queryClient = useQueryClient();
  const [selectedContact, setSelectedContact] = useState<ContactSummaryDto | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    phoneNumbers: string[];
    emailAddresses: string[];
  }>({
    name: "",
    phoneNumbers: [],
    emailAddresses: [],
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showStatus, setShowStatus] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({
    phoneNumbers: [],
    emailAddresses: [],
  });
  
  const updateContactMutation = usePutApiContacts({
    mutation: {
      onSuccess: (data) => {
        if (data !== null && data.id !== undefined) {
          if (props.customerNumber) {
            const queryKey = getGetApiContactsCustomerCustomerNumberQueryKey(props.customerNumber);
            queryClient.setQueryData(queryKey, (previous: ContactSummaryDto[] | undefined) =>
              previous?.map((contact) => (contact.id === data.id ? data : contact)) ?? [data]
            );
            queryClient.invalidateQueries({ queryKey });
          }
          setStatusMessage("Kontakten har uppdaterats");
          setShowStatus(true);
          setSelectedContact(data);
          setFormData({
            name: data.name || "",
            phoneNumbers: [...(data.phoneNumbers || [])],
            emailAddresses: [...(data.emailAddresses || [])],
          });
          props.setCustomerContactList((prevContacts) =>
            prevContacts.map((contact) =>
              contact.id === data.id ? data : contact
            )
          );
          props.setIsContactListUpdated(true);
        }
      },
      onError: () => {
        setStatusMessage("Kunde inte uppdatera kontakten");
        setShowStatus(true);
      },
    },
  });
  
  const deleteContactMutation = useDeleteApiContactsContactId({
    mutation: {
      onSuccess: () => {
        setStatusMessage("Kontakten har tagits bort.");
        setShowStatus(true);
        if (selectedContact?.id !== undefined) {
          if (props.customerNumber) {
            const queryKey = getGetApiContactsCustomerCustomerNumberQueryKey(props.customerNumber);
            queryClient.setQueryData(queryKey, (previous: ContactSummaryDto[] | undefined) =>
              previous?.filter((contact) => contact.id !== selectedContact.id) ?? []
            );
            queryClient.invalidateQueries({ queryKey });
          }
          props.setSelectedContactsIds((prevIds) =>
            prevIds.filter((id) => id !== selectedContact.id)
          );
          props.setCustomerContactList((prevContacts) =>
            prevContacts.filter((contact) => contact.id !== selectedContact.id)
          );
        }
        setSelectedContact(null);
        props.setIsContactListUpdated(true);
      },
      onError: () => {
        setStatusMessage("Ett oväntat svar från servern.");
        setShowStatus(true);
      },
    },
  });

  // Phone number validation regex
  const phoneRegex = /^[\d\s+()-]{6,}$/;
  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (showStatus) {
      const timer = setTimeout(() => {
        setShowStatus(false);
        setStatusMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showStatus]);

  const handleSelect = (contact: ContactSummaryDto) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name || "",
      phoneNumbers: [...(contact.phoneNumbers || [])],
      emailAddresses: [...(contact.emailAddresses || [])],
    });
    setShowDeleteConfirm(false);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      phoneNumbers: [],
      emailAddresses: [],
    };
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = "Namn måste anges";
      isValid = false;
    }

    // Validate phone numbers
    formData.phoneNumbers.forEach((phone, index) => {
      if (phone.trim() && !phoneRegex.test(phone.trim())) {
        newErrors.phoneNumbers[index] = "Ogiltigt telefonnummer";
        isValid = false;
      } else {
        newErrors.phoneNumbers[index] = ""; // Clear error if valid
      }
    });

    // Validate email addresses
    formData.emailAddresses.forEach((email, index) => {
      if (email.trim() && !emailRegex.test(email.trim())) {
        newErrors.emailAddresses[index] = "Ogiltig e-postadress";
        isValid = false;
      } else {
        newErrors.emailAddresses[index] = ""; // Clear error if valid
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (
    field: 'name' | 'phoneNumbers' | 'emailAddresses',
    value: string,
    index?: number
  ) => {
    if (field === 'name') {
      setFormData(prev => ({ ...prev, name: value }));
      // Clear name error when user starts typing
      setErrors(prev => ({ ...prev, name: undefined }));
    } else if (typeof index === 'number') {
      setFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return { ...prev, [field]: newArray };
      });
      // Clear specific field error when user starts typing
      setErrors(prev => {
        const newFieldErrors = [...prev[field]];
        newFieldErrors[index] = '';
        return { ...prev, [field]: newFieldErrors };
      });
    }
  };

  const handleAddField = (field: 'phoneNumbers' | 'emailAddresses') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ""]
    }));
  };

  const handleRemoveField = (field: 'phoneNumbers' | 'emailAddresses', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;

    if (!validateForm()) {
      return;
    }

    const updatedContact: ContactUpdateDto = {
      id: selectedContact.id,
      name: formData.name.trim(),
      phoneNumbers: formData.phoneNumbers.filter(p => p.trim()),
      emailAddresses: formData.emailAddresses.filter(e => e.trim()),
    };

    updateContactMutation.mutate({ data: updatedContact });
  };

  const handleDeleteContact = async () => {
    if (selectedContact?.id) {
      deleteContactMutation.mutate({ contactId: selectedContact.id });
    }
    setShowDeleteConfirm(false);
  };

  return (
    <Modal show={props.isModalEditContactVisible} onHide={() => props.setIsModalEditContactVisible(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Ändra kontakt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={handleSave}>
          {/* Contact Selection */}
          <Row className="mb-4">
            <Col>
              <Dropdown>
                <Dropdown.Toggle as={StbButton} variant="primary" id="dropdown-edit-contact">
                  <span style={!selectedContact?.imported ? {fontStyle: 'italic'} : {}}>
                    {selectedContact?.name || "Välj kontakt"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {props.customerContactList.map((contact: ContactSummaryDto) => (
                    <Dropdown.Item
                      key={contact.id}
                      onClick={() => handleSelect(contact)}
                    >
                      <span style={!contact.imported ? {fontStyle: 'italic', color: '#555'} : {}}>
                        {contact.name}
                      </span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            
            {selectedContact && (
              <Col className="d-flex justify-content-end">
                {selectedContact.imported ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip title="Ta bort kontakt">
                      <StbButton
                        variant="danger"
                        icon
                        onClick={() => setShowDeleteConfirm(true)}
                        aria-label="Ta bort kontakt"
                      >
                        <DeleteOutlined aria-hidden="true" />
                      </StbButton>
                    </Tooltip>
                    <span className="stb-status-badge" title="Importerad" aria-label="Importerad">
                      <ImportOutlined aria-hidden="true" />
                    </span>
                  </div>
                ) : (
                  <Tooltip title="Ta bort kontakt">
                    <StbButton
                      variant="danger"
                      icon
                      onClick={() => setShowDeleteConfirm(true)}
                      aria-label="Ta bort kontakt"
                    >
                      <DeleteOutlined aria-hidden="true" />
                    </StbButton>
                  </Tooltip>
                )}
              </Col>
            )}
          </Row>

          {/* Contact Form */}
          {selectedContact && !selectedContact.imported && (
            <>
              {/* Name Field */}
              <Form.Group className="mb-3">
                <Form.Label>Namn</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  isInvalid={!!errors.name}
                />
                {errors.name && (
                  <div className="text-danger mt-1">
                    {errors.name}
                  </div>
                )}
              </Form.Group>

              {/* Phone Numbers */}
              <Form.Group className="mb-3">
                <Form.Label>Telefonnummer</Form.Label>
                {formData.phoneNumbers.map((phone, index) => (
                  <div key={index} className="mb-2">
                    <div className="d-flex">
                      <Form.Control
                        type="tel"
                        value={phone}
                        onChange={(e) => handleInputChange('phoneNumbers', e.target.value, index)}
                        isInvalid={!!errors.phoneNumbers[index]}
                        placeholder="t.ex. +46 123 456 789"
                      />
                      <StbButton
                        variant="danger"
                        icon
                        className="ms-2"
                        type="button"
                        onClick={() => handleRemoveField('phoneNumbers', index)}
                        aria-label="Ta bort telefonnummer"
                      >
                        <DeleteOutlined aria-hidden="true" />
                      </StbButton>
                    </div>
                    {errors.phoneNumbers[index] && (
                      <div className="text-danger mt-1">
                        {errors.phoneNumbers[index]}
                      </div>
                    )}
                  </div>
                ))}
                <StbButton
                  variant="outline"
                  onClick={() => handleAddField('phoneNumbers')}
                  type="button"
                >
                  Lägg till telefonnummer
                </StbButton>
              </Form.Group>

              {/* Email Addresses */}
              <Form.Group className="mb-3">
                <Form.Label>E-postadresser</Form.Label>
                {formData.emailAddresses.map((email, index) => (
                  <div key={index} className="mb-2">
                    <div className="d-flex">
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => handleInputChange('emailAddresses', e.target.value, index)}
                        isInvalid={!!errors.emailAddresses[index]}
                        placeholder="exempel@domain.com"
                      />
                      <StbButton
                        variant="danger"
                        icon
                        className="ms-2"
                        type="button"
                        onClick={() => handleRemoveField('emailAddresses', index)}
                        aria-label="Ta bort e-postadress"
                      >
                        <DeleteOutlined aria-hidden="true" />
                      </StbButton>
                    </div>
                    {errors.emailAddresses[index] && (
                      <div className="text-danger mt-1">
                        {errors.emailAddresses[index]}
                      </div>
                    )}
                  </div>
                ))}
                <StbButton
                  variant="outline"
                  onClick={() => handleAddField('emailAddresses')}
                  type="button"
                >
                  Lägg till e-postadress
                </StbButton>
              </Form.Group>

              <div className="stb-action-bar">
                <StbButton type="submit" variant="primary">
                  Spara ändringar
                </StbButton>
              </div>
            </>
          )}

                  {/* Show status messages at the top */}
          {showStatus && (
            <Alert 
              variant={statusMessage.includes("korrigera") || statusMessage.includes("inte") ? "danger" : "success"} 
              className="mb-3"
            >
              {statusMessage}
            </Alert>
          )}
          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <Alert variant="danger" className="mt-3">
              <p>Är du säker på att du vill ta bort {selectedContact?.name}?</p>
              <div>
                <StbButton
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  type="button"
                >
                  Avbryt
                </StbButton>
                <StbButton
                  variant="danger"
                  className="ms-2"
                  onClick={handleDeleteContact}
                  type="button"
                >
                  Ta bort
                </StbButton>
              </div>
            </Alert>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ModalEditContact;